import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Navigation, Star, Share2, Clock, X, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ridesApi, errorMessage, titleCase } from "../../lib/api";

const ACTIVE = ["searching", "fee_offer", "driver_assigned", "driver_arriving", "in_progress", "on_the_way"];

// Smoothly-ticking ETA: re-anchors on each fresh GPS fix, counts down per second.
function useSmoothEta(etaSec) {
  const [left, setLeft] = useState(etaSec ?? null);
  const endRef = useRef(null);
  useEffect(() => {
    if (etaSec == null) { endRef.current = null; setLeft(null); return; }
    endRef.current = Date.now() + etaSec * 1000;
    setLeft(etaSec);
    const t = setInterval(() => setLeft(Math.max(0, Math.round((endRef.current - Date.now()) / 1000))), 1000);
    return () => clearInterval(t);
  }, [etaSec]);
  return left;
}

const fmt = (s) => (s == null ? null : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);

// Pinned bar above the tracking map: driver + vehicle, a smooth "arriving in m:ss"
// progress bar, and a toggle to share / stop sharing the live trip with family.
export default function DriverEtaBar({ ride }) {
  const [loc, setLoc] = useState(null);
  const [sharing, setSharing] = useState(!!ride?.share_token && !ride?.share_revoked);
  const [busy, setBusy] = useState(false);
  const peak = useRef({});
  const driver = ride?.driver;
  const etaSec = loc?.eta_sec ?? null;
  const smoothEta = useSmoothEta(ACTIVE.includes(ride?.status) ? etaSec : null);

  useEffect(() => { setSharing(!!ride?.share_token && !ride?.share_revoked); }, [ride?.share_token, ride?.share_revoked]);

  useEffect(() => {
    if (!ride || !ACTIVE.includes(ride.status)) return;
    let on = true;
    const poll = () => ridesApi.driverLocation(ride.ride_id).then((d) => { if (on) setLoc(d); }).catch(() => {});
    poll();
    const t = setInterval(poll, 5000);
    return () => { on = false; clearInterval(t); };
  }, [ride?.ride_id, ride?.status]);

  if (!ride || !driver || !ACTIVE.includes(ride.status)) return null;

  const toPickup = ["searching", "fee_offer", "driver_assigned", "driver_arriving"].includes(ride.status);
  const etaMin = loc?.eta_min ?? ride.eta_min ?? null;
  const phaseKey = toPickup ? "pickup" : "drop";
  if (etaMin != null) peak.current[phaseKey] = Math.max(peak.current[phaseKey] || 0, etaMin);
  const peakVal = peak.current[phaseKey] || etaMin || 1;
  const progress = etaMin != null ? Math.min(100, Math.max(6, Math.round((1 - etaMin / Math.max(peakVal, 1)) * 100))) : 10;

  const toggleShare = async () => {
    setBusy(true);
    try {
      if (sharing) { await ridesApi.unshare(ride.ride_id); setSharing(false); toast("Live trip sharing stopped"); }
      else {
        const { url } = await ridesApi.share(ride.ride_id);
        setSharing(true);
        if (navigator.share) { try { await navigator.share({ title: "Track my trip", text: "Follow my live KohimaTaxi trip:", url }); } catch { /* user dismissed */ } }
        else { await navigator.clipboard.writeText(url); toast.success("Trip link copied — share it with family"); }
      }
    } catch (e) { toast.error(errorMessage(e, "Could not update sharing")); } finally { setBusy(false); }
  };

  return (
    <div className="kt-card p-4" data-testid="driver-eta-bar">
      <div className="flex items-center gap-3">
        <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-brand text-white kt-pulse-ring"><Navigation className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-ink">{driver.name}</p>
          <p className="truncate text-xs text-ink-soft">
            {[driver.vehicle || titleCase(driver.vehicle_type || ride.vehicle_type || ""), driver.plate].filter(Boolean).join(" · ")}
            {driver.rating ? <span className="ml-1 inline-flex items-center gap-0.5 text-amber-500"><Star className="h-3 w-3 fill-amber-400" /> {driver.rating}{driver.trips ? <span className="text-ink-muted"> · {driver.trips.toLocaleString()} trips</span> : null}</span> : null}
          </p>
        </div>
        <button onClick={toggleShare} disabled={busy} data-testid="share-trip-btn" className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold ${sharing ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-surface-100 text-ink hover:bg-surface-200"}`}>
          {sharing ? <><X className="h-3.5 w-3.5" /> Stop sharing</> : <><Share2 className="h-3.5 w-3.5" /> Share trip</>}
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        {driver.phone && <a href={`tel:${driver.phone}`} data-testid="eta-call-driver" className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-brand py-2 text-xs font-bold text-white hover:bg-brand-600"><Phone className="h-3.5 w-3.5" /> Call driver</a>}
        <Link to={`/app/chat/${ride.ride_id}`} data-testid="eta-message-driver" className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-surface-200 py-2 text-xs font-bold text-ink hover:bg-surface-50"><MessageCircle className="h-3.5 w-3.5" /> Message</Link>
      </div>
      {toPickup && etaMin != null && etaMin <= 1 && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2" data-testid="pickup-nudge-banner">
          <span className="text-base animate-bounce">🚕</span>
          <p className="text-xs font-bold text-amber-800">Your driver is almost here — head to your pickup point.</p>
        </div>
      )}
      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1 font-semibold text-ink" data-testid="driver-eta-text">
            <Clock className="h-3.5 w-3.5 text-brand" /> {toPickup
              ? (smoothEta != null && smoothEta <= 0 ? "Driver is almost at your pickup" : (smoothEta != null ? `Driver arriving in ${fmt(smoothEta)}` : "On the way to you"))
              : (smoothEta != null ? `To your destination · ${fmt(smoothEta)}` : "Heading to your destination")}
          </span>
          <span className="text-ink-muted">{titleCase(ride.status)}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-surface-200">
          <div className="h-full rounded-full bg-brand transition-[width] duration-1000 ease-linear" style={{ width: `${progress}%` }} />
        </div>
        {sharing ? <p className="mt-2 text-[11px] text-ink-muted" data-testid="sharing-on-note">🔗 Live trip sharing is on — family can follow your ride.</p> : null}
      </div>
    </div>
  );
}
